import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import { TIME_ZONES_LIST, TIME_ZONE_FAMILIES } from '../timezones/time-zones.const';
import { TimestampsService } from '../timestamps.service';
import { DetectMethodChanges, DetectSetterChanges } from '../utils';
import { AdjustmentPricingGridSettingsModel } from './model/AdjustmentPricingGridSettings';
import { AppSettingsUpdatedMessageTopic, ApplicationSettingsService } from './application-settings.service';
import { LocationService } from '../location.service';
import { TradingInstrumentsService } from '../trading-instruments/trading-instruments-service.interface';
import { SessionService } from '../authentication/session-service.service';
import { MessageBusService } from '../message-bus.service';
import { TimeZoneDescriptor } from '../timezones/time-zone-descriptor.interface';
import { AccessControlService } from '../access-control-service.class';
import {ExpectedMoveSettingsComponent} from "../expected-move/expected-move-settings.component";
import {PanelAliasesDialogComponent} from '../workspace/panel-aliases/panel-aliases-dialog.component';
import {WorkspaceSecurityContextService} from '../workspace/workspace-security-context.service';
import {ShellClientService} from "../shell-communication/shell-client.service";
import {
   GetSpxPriceAdjustmentOutsideOfMarketHoursShell, GetSpxPriceAdjustmentOutsideOfMarketHoursShellReply
} from "../shell-communication/shell-operations-protocol";

@Component({
   selector: 'ets-app-settings',
   templateUrl: 'app-settings.component.html',
   styleUrls: ['./app-settings.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
   providers: [WorkspaceSecurityContextService]
})
export class AppSettingsComponent implements OnInit {
   constructor(
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _timestampsService: TimestampsService,
      private readonly _applicationSettingsService: ApplicationSettingsService,
      private readonly _locationService: LocationService,
      private readonly _tiService: TradingInstrumentsService,
      private readonly _sessionService: SessionService,
      private readonly _messageBus: MessageBusService,
      private readonly _accessControlService: AccessControlService,
      private readonly _shellService: ShellClientService,
      private readonly _workspaceSecurity: WorkspaceSecurityContextService
   ) {
   }

   //
   private _isVisible = false;
   get isVisible() {
      return this._isVisible;
   }
   @DetectSetterChanges()
   set isVisible(v: boolean) {
      this._isVisible = v;
   }

   //
   private _isLoading = false;
   get isLoading() {
      return this._isLoading;
   }
   @DetectSetterChanges()
   set isLoading(v: boolean) {
      this._isLoading = v;
   }

   //
   timestampsZoneItems: string[] = TIME_ZONES_LIST;

   //
   private _timezones : TimeZoneDescriptor[];
   get timezones() : TimeZoneDescriptor[] {
      return this._timezones;
   }
   @DetectSetterChanges()
   set timezones(v : TimeZoneDescriptor[]) {
      this._timezones = v;
   }
   
   //
   private _timestampsDefaultZone: string;
   get timestampsDefaultZone() {
      return this._timestampsDefaultZone;
   }
   @DetectSetterChanges()
   set timestampsDefaultZone(v: string) {
      this._timestampsDefaultZone = v;
   }

   //
   adjustmentPricingGrid: AdjustmentPricingGridSettingsModel;

   get showSpxAdjustmentSetting() {
      return this._sessionService.isSuperUser;
   }

   //
   adjustSpxPriceOutsideOfMarketHours = false;

   preferredTrendTimeframe: '1 min' | '5 min' | '15 min';

   @ViewChild(ExpectedMoveSettingsComponent)
   expectedMoveSettingsComponent: ExpectedMoveSettingsComponent;

   // CR-20260928-002 — Panel Aliases group (gated by its own secure element).
   @ViewChild(PanelAliasesDialogComponent)
   panelAliasesDialog: PanelAliasesDialogComponent;

   get showPanelAliases(): boolean {
      return this._workspaceSecurity.panelAliases;
   }

   openPanelAliases(): void {
      this.panelAliasesDialog?.show();
   }


   //
   @DetectMethodChanges()
   ngOnInit() {
      this.restoreSettings();
   }

   //
   @DetectMethodChanges()
   onHidden() {
      this.timestampsDefaultZone = undefined;
      this.isVisible = false;
      this.expectedMoveSettingsComponent?.reset();
   }

   //
   @DetectMethodChanges()
   onShown() {
      this.restoreSettings();
   }

   private restoreSettings() {
      this.restoreTimezoneSettings();
      this.restoreApgSettings();
      this.restoreExpectedMoveSettings();
      this.restoreMiscSettings();
   }

//
   @DetectMethodChanges()
   saveChangesAndClose() {
      this.saveChanges();
      this.close();
   }

   //
   @DetectMethodChanges()
   close() {
      this.isVisible = false;
   }

   //
   @DetectMethodChanges()
   onChanges() {
   }

   //
   private saveChanges() {
      this.saveTimezoneSettings();
      this.saveAdjustmentPricingGridSettings();
      this.saveExpectedMoveSettings();
      this.saveMiscSettings().then(() => {});
      
      this._messageBus.publishAsync({
         topic: AppSettingsUpdatedMessageTopic,
         payload: {}
      });
   }

   //
   private saveAdjustmentPricingGridSettings() {
      this._applicationSettingsService.adjustmentPricingGrid = this.adjustmentPricingGrid;
   }

   //
   private saveTimezoneSettings() {
      this._applicationSettingsService.defaultTimezone = this.timestampsDefaultZone;
   }

   //
   private restoreApgSettings() {

      try {
         const apgSettings =
             this._applicationSettingsService.adjustmentPricingGrid;

         this.adjustmentPricingGrid = new AdjustmentPricingGridSettingsModel(
             this._locationService,
             this._tiService,
             this._sessionService,
             this._accessControlService,
             apgSettings,
             this._changeDetector
         );
      } catch(e) {
         console.error(e);
      }

   }

   //
   private restoreTimezoneSettings() {
      try {
         this.timezones = TIME_ZONE_FAMILIES.flatMap(x => x.timeZoneDescriptors);
         this.timestampsDefaultZone = this._timestampsService.getDefaultTimezone();
      } catch(e) {
         console.error(e);
      }
   }

   @DetectMethodChanges()
   onCharlesSchwabFixChanged() {
      if (this.adjustmentPricingGrid.useCharlesSchwabFix) {
         this.adjustmentPricingGrid.useMarginEfficientAdjustment = false;
      }
   }

   @DetectMethodChanges()
   onTimezoneChanged($event: string) {

   }

   private saveExpectedMoveSettings() {
      this._applicationSettingsService.expectedMoveSettings
          = this.expectedMoveSettingsComponent.getSettings();
   }

   private restoreExpectedMoveSettings() {
      if (!this.expectedMoveSettingsComponent) {
         return;
      }
      try {
         this.expectedMoveSettingsComponent.init();
      } catch(e) {
         console.error(e);
      }
   }



   private async saveMiscSettings() {
      this._applicationSettingsService.adjustSpxPriceOutsideOfMarketHours =
          this.adjustSpxPriceOutsideOfMarketHours;

      this._applicationSettingsService.preferredTrendTimeframe =
          this.preferredTrendTimeframe;
   }

   private restoreMiscSettings() {
      try {
         this.adjustSpxPriceOutsideOfMarketHours = this._applicationSettingsService.adjustSpxPriceOutsideOfMarketHours;
         this.preferredTrendTimeframe = this._applicationSettingsService.preferredTrendTimeframe as any;
      } catch(e) {
         console.error(e);
      }
   }
}
